import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTonConnectUI } from '@tonconnect/ui-react';
import { Alert, Button, Card, Icon, Skeleton } from '@tb/ui';
import type { PublishPlan, SiteVersion } from '@tb/shared';
import { api } from '../api/endpoints';
import { RequestError } from '../api/client';
import { Screen } from '../components/Layout';
import { PaywallAlert, isPaywallError } from '../components/PaywallAlert';
import { StatusBadge } from '../components/StatusBadge';
import { useT } from '../i18n/LocaleProvider';
import { formatBytes, shortenBagId } from '../lib/format';
import { useRuntimeConfig } from '../RuntimeConfigProvider';
import { notify } from '../telegram/telegram';

type Stage =
  | { name: 'idle' }
  | { name: 'building'; versionId: string }
  | { name: 'ready'; plan: PublishPlan }
  | { name: 'signing'; plan: PublishPlan }
  | { name: 'confirming'; versionId: string }
  | { name: 'paywalled'; error: RequestError }
  | { name: 'done' }
  | { name: 'error'; message: string };

export function PublishScreen() {
  const { siteId = '' } = useParams();
  const navigate = useNavigate();
  const config = useRuntimeConfig();
  const queryClient = useQueryClient();
  const [tonConnectUI] = useTonConnectUI();
  const [stage, setStage] = useState<Stage>({ name: 'idle' });
  const t = useT();

  const siteQuery = useQuery({ queryKey: ['site', siteId], queryFn: () => api.sites.get(siteId) });

  const startMutation = useMutation({
    mutationFn: () => api.publish.start(siteId),
    onSuccess: (version) => setStage({ name: 'building', versionId: version.id }),
    onError: (error: Error) => {
      if (isPaywallError(error)) {
        setStage({ name: 'paywalled', error });
        return;
      }
      setStage({ name: 'error', message: error.message });
    },
  });

  const buildingId = stage.name === 'building' ? stage.versionId : null;
  const versionQuery = useQuery({
    queryKey: ['version', siteId, buildingId],
    queryFn: () => api.sites.version(siteId, buildingId!),
    enabled: buildingId !== null,
    refetchInterval: 2500,
  });

  useEffect(() => {
    const version = versionQuery.data;
    if (!version || stage.name !== 'building') return;

    if (version.status === 'FAILED') {
      setStage({ name: 'error', message: version.error ?? 'The build failed.' });
      return;
    }

    if (version.status === 'BUILT' || version.bagId) {
      void (async () => {
        try {
          const plan = await api.publish.plan(siteId, version.id);
          setStage({ name: 'ready', plan });
        } catch (error) {
          setStage({
            name: 'error',
            message: error instanceof RequestError ? error.message : String(error),
          });
        }
      })();
    }
  }, [versionQuery.data, stage.name, siteId]);

  const confirmingId = stage.name === 'confirming' ? stage.versionId : null;
  const confirmQuery = useQuery({
    queryKey: ['version', siteId, confirmingId, 'confirm'],
    queryFn: () => api.sites.version(siteId, confirmingId!),
    enabled: confirmingId !== null,
    refetchInterval: 4000,
  });

  useEffect(() => {
    const version = confirmQuery.data;
    if (!version || stage.name !== 'confirming') return;

    if (version.status === 'LIVE') {
      notify('success');
      void queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      setStage({ name: 'done' });
    } else if (version.status === 'FAILED') {
      notify('error');
      setStage({ name: 'error', message: version.error ?? 'The publish did not complete.' });
    }
  }, [confirmQuery.data, stage.name, siteId, queryClient]);

  const sign = async (plan: PublishPlan) => {
    setStage({ name: 'signing', plan });
    try {
      const result = await tonConnectUI.sendTransaction({
        validUntil: plan.transaction.validUntil,
        network: plan.transaction.network as never,
        messages: plan.transaction.messages,
      });

      const version: SiteVersion = await api.publish.confirm(siteId, plan.versionId, {
        boc: result.boc,
      });
      setStage({ name: 'confirming', versionId: version.id });
    } catch (error) {
      const message = error instanceof RequestError ? error.message : t('publish.notSent');
      setStage({ name: 'error', message });
    }
  };

  if (siteQuery.isPending) {
    return (
      <Screen back={`/sites/${siteId}`}>
        <div className="tb-screen">
          <Skeleton height={140} />
        </div>
      </Screen>
    );
  }

  const site = siteQuery.data;

  return (
    <Screen title={t('publish.title')} back={`/sites/${siteId}`}>
      <div className="tb-screen">
        {stage.name === 'done' ? (
          <>
            <Alert tone="success" title={t('publish.done.title', { domain: site?.domain ?? '' })}>
              {t('publish.done.body')}
            </Alert>
            <Button icon="chevron-right" block onClick={() => void navigate(`/sites/${siteId}`)}>
              {t('publish.done.back')}
            </Button>
          </>
        ) : null}

        {stage.name === 'paywalled' ? <PaywallAlert error={stage.error} siteId={siteId} /> : null}

        {stage.name === 'error' ? (
          <>
            <Alert tone="danger" title={t('publish.stopped')}>
              {stage.message}
            </Alert>
            <Button icon="refresh" block onClick={() => setStage({ name: 'idle' })}>
              {t('publish.startOver')}
            </Button>
          </>
        ) : null}

        {stage.name === 'idle' ? (
          <>
            <Card>
              <span className="tb-label">{t('publish.what')}</span>
              <Step
                icon="bag"
                title={t('publish.step.pack.title')}
                text={t('publish.step.pack.body')}
              />
              <Step
                icon="wallet"
                title={t('publish.step.sign.title')}
                text={t('publish.step.sign.body', { amount: config.publishAmountTon })}
              />
              <Step
                icon="domain"
                title={t('publish.step.point.title')}
                text={t('publish.step.point.body')}
              />
            </Card>

            <Alert tone="info">{t('publish.noKeys', { domain: site?.domain ?? '' })}</Alert>

            <Button
              icon="publish"
              block
              loading={startMutation.isPending}
              onClick={() => startMutation.mutate()}
            >
              {t('publish.pack')}
            </Button>
          </>
        ) : null}

        {stage.name === 'building' ? (
          <Card>
            <div className="tb-row">
              <Icon name="spinner" size={20} spin />
              <span className="tb-grow">{t('publish.packing')}</span>
              <StatusBadge status="BUILDING" />
            </div>
            <span className="tb-hint">{t('publish.packingHint')}</span>
          </Card>
        ) : null}

        {stage.name === 'ready' || stage.name === 'signing' ? (
          <>
            <Card>
              <span className="tb-label">{t('publish.ready')}</span>
              <div className="kv">
                <span className="kv__key">{t('publish.field.domain')}</span>
                <span className="kv__value">{site?.domain}</span>
              </div>
              <div className="kv">
                <span className="kv__key">{t('publish.field.bag')}</span>
                <span className="kv__value tb-mono">{shortenBagId(stage.plan.bagId)}</span>
              </div>
              <div className="kv">
                <span className="kv__key">{t('publish.field.size')}</span>
                <span className="kv__value">
                  {t('publish.size', {
                    size: formatBytes(stage.plan.sizeBytes),
                    count: stage.plan.fileCount,
                  })}
                </span>
              </div>
              <div className="kv">
                <span className="kv__key">{t('publish.field.gas')}</span>
                <span className="kv__value">
                  {t('publish.gasUpTo', { amount: stage.plan.feeEstimateTon })}
                </span>
              </div>
            </Card>

            <Button
              icon="wallet"
              block
              loading={stage.name === 'signing'}
              onClick={() => void sign(stage.plan)}
            >
              {t('publish.sign')}
            </Button>
          </>
        ) : null}

        {stage.name === 'confirming' ? (
          <Card>
            <div className="tb-row">
              <Icon name="clock" size={20} />
              <span className="tb-grow">{t('publish.confirming')}</span>
              <StatusBadge status="PUBLISHING" />
            </div>
            <span className="tb-hint">{t('publish.confirmingHint')}</span>
          </Card>
        ) : null}
      </div>
    </Screen>
  );
}

function Step({
  icon,
  title,
  text,
}: {
  icon: 'bag' | 'wallet' | 'domain';
  title: string;
  text: string;
}) {
  return (
    <div className="tb-row" style={{ alignItems: 'flex-start' }}>
      <Icon name={icon} size={18} />
      <div className="tb-stack" style={{ gap: 2 }}>
        <span style={{ fontWeight: 600 }}>{title}</span>
        <span className="tb-hint">{text}</span>
      </div>
    </div>
  );
}
